import Profile from "ol-ext/control/Profile";
import Hover from "ol-ext/interaction/Hover";
import { Vector as VectorSource } from "ol/source";
import { Vector as VectorLayer } from "ol/layer";
import { Feature, Map } from "ol";
import { LineString, MultiLineString, Point } from "ol/geom";
import { profileStyle, selectedTrackStyle, trackStyle } from "../Symbols/elevationSymbols";
import { IGroup } from "../../Contexts/SearchContext";
import i18next from "i18next";

interface ITrackOption {
    name: string;
    id: number;
    url: string;
}

export const createTracks = (config): IGroup[] => {
    const trackLayers = config.layers.filter((layer) => layer.elevationProfileLayers);
    if (!trackLayers.length) {
        return;
    }
    const tracks: ITrackOption[] = [];
    trackLayers.map((trackLayer) => {
        trackLayer.elevationProfileLayers.map((id) => {
            trackLayer.layers.map(
                (l) => l.subLayerIds?.includes(id) && tracks.push({ name: l.name, id, url: `${trackLayer.url}/${id}` })
            );
        });
    });

    return [
        { label: "Izvēlieties trasi", value: "placeholder" },
        ...tracks.map((e) => ({
            label: e.name,
            value: e.url
        }))
    ];
};

export const multiLineToLine = (feature: Feature): Feature => {
    const geometry = feature.getGeometry();
    if (geometry.getType() !== "MultiLineString") {
        return feature;
    }

    const coordinates = [];
    (geometry as MultiLineString)
        .getLineStrings()
        .forEach((lineString) => lineString.getCoordinates().forEach((coordinate) => coordinates.push(coordinate)));

    feature.setGeometry(new LineString(coordinates));
    return feature;
};

export const getParsedFeature = (feature: Feature): Feature<LineString> =>
    new Feature(
        new LineString(
            (feature.getGeometry() as LineString).getCoordinates().map((c) => [c[0], c[1], Number(c[2].toFixed(1))])
        )
    );

export const createProfileLayer = (source: VectorSource): VectorLayer<VectorSource> =>
    new VectorLayer({
        source,
        style: (f) => (f.get("select") ? selectedTrackStyle : trackStyle),
        properties: { id: "elevationProfileLayer" },
        zIndex: 50000
    });

export const createProfile = (target: HTMLDivElement): Profile =>
    new Profile({
        target,
        selectable: true,
        zoomable: true,
        style: profileStyle,
        width: 500,
        height: 200,
        info: {
            xtitle: "Distance",
            ytitle: i18next.t("elevation.altitude"),
            altitude: i18next.t("elevation.altitude"),
            altitudeUnits: "m",
            distanceUnitsKM: "km",
            distanceUnitsM: "m"
        }
    });

export const addProfileData = async (map: Map, targetRef: HTMLDivElement, source: VectorSource, feature?: Feature) => {
    const prof = map
        .getControls()
        .getArray()
        .find((control: any) => control?.info);

    let profile;
    if (prof) {
        map.removeControl(prof);
        profile = createProfile(targetRef);
        map.addControl(profile);
    }

    if (!feature) {
        return;
    }

    profile.setGeometry(getParsedFeature(feature), { graduation: 50 });
    const marker = new Feature(new Point([0, 0]));
    marker.setStyle([]);
    source.clear();
    source.addFeature(feature);
    source.addFeature(marker);

    // Draw a point on the map when mouse fly over profile
    const drawPoint = (e) => {
        if (!marker) {
            return;
        }
        if (e.type == "over") {
            marker.setGeometry(new Point(e.coord));
            marker.setStyle(null);
        } else {
            marker.setStyle([]);
        }
    };
    // Show a popup on over
    // @ts-ignore
    profile.on(["over", "out"], drawPoint);

    // Show selection
    let start = 0;
    let selection;
    // @ts-ignore
    profile.on("click", () => {
        if (selection) {
            source.removeFeature(selection);
            selection = false;
        }
    });
    // @ts-ignore
    profile.on("dragstart", (e: any) => {
        start = e.index;
    });
    // @ts-ignore
    profile.on(["dragend", "dragging"], (e) => {
        // @ts-ignore
        const track = profile.getSelection(start, e.index);
        if (selection) {
            selection.getGeometry().setCoordinates(track);
        } else {
            selection = new Feature(new LineString(track));
            selection.set("select", true);
            source.addFeature(selection);
        }
    });
    // @ts-ignore
    profile.on("zoom", (e: any) =>
        setTimeout(() => {
            if (selection) {
                source.removeFeature(selection);
            }
            if (e.geometry) {
                selection = new Feature(e.geometry);
                selection.set("select", true);
                source.addFeature(selection);
            } else {
                selection = null;
            }
        })
    );

    // Show on map over
    const hover = new Hover({ cursor: "pointer", hitTolerance: 10, handleEvent: undefined });
    hover.on("hover", (e) => {
        drawPoint({ type: "over", coord: feature.getGeometry().getClosestPoint(e.coordinate) });
        profile.showAt(e.coordinate);
    });
    hover.on("leave", () => drawPoint({}));
    map.addInteraction(hover);
};

export const removeDiacritics = (str: string): string => str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
